import { useState, useEffect, useMemo, useContext, useCallback } from "react";
import { Col, Row } from "react-bootstrap";
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Skeleton } from "primereact/skeleton";

// Icons
import iconCourses from "../../../assets/icons/CoursesIcon.svg";
import iconDocuments from "../../../assets/icons/DocumentIcon.svg";
import iconCatalogue from "../../../assets/icons/CatalogueIcon.svg";
import iconCyberRange from "../../../assets/icons/CyberRangeIcon.svg";
import iconSupport from "../../../assets/icons/SupportIcon.svg";
import iconAssessment from "../../../assets/icons/AssessmentIcon.svg";
import iconQuiz from "../../../assets/icons/QuizIcon.svg";
import iconReport from "../../../assets/icons/ReportIcon.svg";
import iconTickets from "../../../assets/icons/MyTicketIcon.svg";
import iconFaq from "../../../assets/icons/FaqIcon.svg";
// Components
import DashboardCard from "./Cards";
import DocumentBarChart from "./charts/DocumentBarChart";
import TicketsStatusPieChart from "./charts/TicketsStatusPieChart";
import QuizzesLineChart from "./charts/QuizLineChart";
import AssessmentBarChart from "./charts/AssessmentBarchart";
import CoursesStatusChart from "./charts/CourseStatusChart";
import CalendarCard from "./charts/CalendarCard";
import Catalogueschart from "./charts/CataloguesChart";
import LearnerRecentCourseCard from "./charts/LearnerRecentCoursesCard";
import LearnerCatalogueschart from "./charts/LearnerCataloguesChart";
import LearnerTicketStatusPieChart from "./charts/LearnerTIcketStatusChart";
import TotalLabHoursCard from "./charts/TotalLabHoursCard";
import ExpertSessionCard from "./charts/ExpertSessionCard";

import AuthContext from "../../../auth/AuthContext";
import MenuService from "../../../services/MenuService";
import ExpertSessionsService from "../../../services/ExpertSessionsService";

const useWindowWidth = () => {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return width;
};

const STORAGE_KEY = "dashboard_chart_order";

const saveToLocalStorage = (ids) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {}
};

const loadFromLocalStorage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return [];
    const parsed = JSON.parse(saved);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const mergeOrder = (savedIds, initialIds) => {
  const validSaved = savedIds.filter((id) => initialIds.includes(id));
  const missing = initialIds.filter((id) => !validSaved.includes(id));
  return [...validSaved, ...missing];
};

const SortableItem = ({ id, component, colSize, activeId, isCatalogue }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = { transform: CSS.Transform.toString(transform), transition };
  const isDragging = activeId === id;

  return (
    <Col
      xs={12}
      sm={6}
      md={12}
      lg={isCatalogue ? 12 : 6}
      xl={colSize}
      className="mb-2"
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      <div className={`draggable-item ${isDragging ? "dragging" : ""}`}>
        {component}
      </div>
    </Col>
  );
};

const Dashboard = () => {
  const authContext = useContext(AuthContext);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionAvailable, setSessionAvailable] = useState(null);

useEffect(() => {
    const controller = new AbortController(); 

    const checkSession = async () => {
      try {
        const data = await ExpertSessionsService.GetSessionAvailable(
          authContext,
          controller.signal 
        );

        if (controller.signal.aborted) return; 

        if (data?.isSuccess && data?.data === true) {
          setSessionAvailable(true);
        } else {
          setSessionAvailable(false);
        }
      } catch (err) {
        if (err.name === "AbortError") return; 
        setSessionAvailable(false);
      }
    };

    if (authContext?.token) {
      checkSession();
    }

    return () => controller.abort();
  }, [authContext]);

  const checkAccess = useCallback(
    (menuName, accessLevel = 1) => {
      const permissions = authContext.getPermissions(menuName);
      return permissions.some((f) => f.access.includes(accessLevel));
    },
    [authContext]
  );

const loadMenus = useCallback(async (signal) => {
  setLoading(true);

  try {
    let data = await MenuService.GetMenus(
      authContext.userId,
      authContext,
      signal
    );

    if (signal?.aborted) return;
    if (!data) return;

    let cards = [];

    // Hidden menus remove
  let visibleMenus = (data.data || []).filter(
  (menu) => !menu.hiddenMenu
);
    const addCard = (menuName, icon) => {
      if (checkAccess(menuName)) {
        let menu = visibleMenus.find(
          (f) => f.menuName === menuName
        );

        if (menu) {
          cards.push({
            title: menu.displayText,
            icon: icon,
            path: menu.routerLink,
          });
        }
      }
    };

    addCard("courses", iconCourses);
    addCard("documentdashboard", iconDocuments);
    addCard("cataloguedashboard", iconCatalogue);
    addCard("bays", iconCyberRange);
    addCard("assessmentdashboard", iconAssessment);
    addCard("quizdashboard", iconQuiz);
    addCard("support", iconSupport);
    addCard("ticketdashboard", iconTickets);
    addCard("report", iconReport);
    addCard("faq", iconFaq);

    setCards(cards);
  } catch (error) {
    if (error.name === "AbortError") return;

    console.error("Menu load failed:", error);
  } finally {
    if (!signal?.aborted) setLoading(false);
  }
}, [checkAccess, authContext]);

useEffect(() => {
    const controller = new AbortController(); 
    loadMenus(controller.signal); 
    return () => controller.abort();
  }, [loadMenus]);

  const width = useWindowWidth();
  const isLgScreen = width >= 992 && width < 1200;

const availableComponents = useMemo(() => {
  const hasAccess = checkAccess("dashboard", 7);

  if (hasAccess) {
    return [
      { id: "1", component: <CalendarCard />, isCatalogue: false },
      { id: "2", component: <TotalLabHoursCard />, isCatalogue: false },
      ...(sessionAvailable === true
        ? [{ id: "3", component: <ExpertSessionCard />, isCatalogue: false }]
        : []),
      { id: "4", component: <CoursesStatusChart />, isCatalogue: false },
      { id: "5", component: <DocumentBarChart />, isCatalogue: false },
      { id: "6", component: <Catalogueschart />, isCatalogue: true },
      { id: "7", component: <AssessmentBarChart />, isCatalogue: false },
      { id: "8", component: <QuizzesLineChart />, isCatalogue: false },
      { id: "9", component: <TicketsStatusPieChart />, isCatalogue: false },
    ];
  } else {
    return [
      { id: "1", component: <CalendarCard />, isCatalogue: false },
      ...(sessionAvailable === true
        ? [{ id: "3", component: <ExpertSessionCard />, isCatalogue: false }]
        : []),
      { id: "5", component: <DocumentBarChart />, isCatalogue: false },
      { id: "10", component: <LearnerRecentCourseCard />, isCatalogue: false },
      { id: "11", component: <LearnerCatalogueschart />, isCatalogue: true },
      { id: "12", component: <LearnerTicketStatusPieChart />, isCatalogue: false },
    ];
  }
}, [checkAccess, sessionAvailable]);

  const itemsMap = useMemo(
    () => Object.fromEntries(availableComponents.map((item) => [item.id, item])),
    [availableComponents]
  );

  const initialIds = useMemo(
    () => availableComponents.map((i) => i.id),
    [availableComponents]
  );

  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(null);

  const mouseSensor = useSensor(MouseSensor, { activationConstraint: { distance: 8 } });
  const touchSensor = useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } });
  const sensors = useSensors(mouseSensor, touchSensor);

  const handleDragStart = (event) => setActiveId(event.active.id);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;
    if (active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      const newItems = arrayMove(items, oldIndex, newIndex);
      setItems(newItems);
      saveToLocalStorage(newItems.map((i) => i.id)); 
    }
  };

  useEffect(() => {
    if (sessionAvailable === null) return; 
    const savedIds = loadFromLocalStorage();
    const mergedIds = mergeOrder(savedIds, initialIds);
    const nextItems = mergedIds.map((id) => itemsMap[id]).filter(Boolean);
    setItems(nextItems);
    saveToLocalStorage(mergedIds);
  }, [initialIds, itemsMap, sessionAvailable]);

  const getColSizes = (rowItems) => {
    const hasCatalogue = rowItems.some((item) => item.isCatalogue);
    return rowItems.map((item) => (item.isCatalogue ? 6 : hasCatalogue ? 3 : 4));
  };

  const renderChartRows = () => {
    if (isLgScreen) {
      const rows = [];
      let currentRow = [];
      let currentWidth = 0;

      items.forEach((item, idx) => {
        const colLg = item.isCatalogue ? 12 : 6;
        if (currentWidth + colLg > 12) {
          rows.push(
            <Row key={`row-${idx}`} className="mb-2 g-3">
              <SortableContext items={currentRow.map((itm) => itm.id)} strategy={verticalListSortingStrategy}>
                {currentRow.map((itm) => (
                  <SortableItem key={itm.id} id={itm.id} component={itm.component} colSize={itm.isCatalogue ? 6 : 3} activeId={activeId} isCatalogue={itm.isCatalogue} />
                ))}
              </SortableContext>
            </Row>
          );
          currentRow = [];
          currentWidth = 0;
        }
        currentRow.push(item);
        currentWidth += colLg;
      });

      if (currentRow.length > 0) {
        rows.push(
          <Row key="last-row" className="mb-2 g-3">
            <SortableContext items={currentRow.map((itm) => itm.id)} strategy={verticalListSortingStrategy}>
              {currentRow.map((itm) => (
                <SortableItem key={itm.id} id={itm.id} component={itm.component} colSize={itm.isCatalogue ? 6 : 3} activeId={activeId} isCatalogue={itm.isCatalogue} />
              ))}
            </SortableContext>
          </Row>
        );
      }
      return rows;
    } else {
      const rows = [];
      for (let i = 0; i < items.length; i += 3) {
        const rowItems = items.slice(i, i + 3);
        const colSizes = getColSizes(rowItems);
        rows.push(
          <Row key={i} className="mb-2 g-3">
            <SortableContext items={rowItems.map((item) => item.id)} strategy={verticalListSortingStrategy}>
              {rowItems.map((item, idx) => (
                <SortableItem key={item.id} id={item.id} component={item.component} colSize={colSizes[idx]} activeId={activeId} isCatalogue={item.isCatalogue} />
              ))}
            </SortableContext>
          </Row>
        );
      }
      return rows;
    }
  };

  return (
    <div className="container-fluid mt-lg-0" style={{ overflow: "hidden" }}>
      {/* Cards Section */}
      <Row className="justify-content-start justify-content-xl-around mb-xl-2 mt-1">
        {loading
          ? Array.from({ length: 10 }).map((_, idx) => (
              <Col key={idx} xs={6} sm={4} md={3} lg={2} xl={1}>
                <div className="d-flex flex-column align-items-center justify-content-center h-100 p-1 pb-3 pb-xl-2">
                  <Skeleton width="40%" height="30px" className="mb-2" />
                  <Skeleton width="80%" height="25px" />
                </div>
              </Col>
            ))
          : cards.map((card, idx) => (
              <Col key={idx} xs={6} sm={4} md={3} lg={2} xl={1}>
                <DashboardCard title={card.title} icon={card.icon} path={card.path} />
              </Col>
            ))}
      </Row>

      {/* Draggable Chart Components */}
      {sessionAvailable === null ? (
        <Row className="g-3">
          {Array.from({ length: 6 }).map((_, idx) => (
            <Col key={idx} xs={12} sm={6} xl={4} className="mb-2">
               <div
      className="charts"
      style={{
        borderRadius: 12,
        padding: 16,
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        width: "100%",
        height: "357px",
        display: "flex",
        border: "1px solid #eee",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
               <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Skeleton width="120px" height="20px" />
            <Skeleton width="100px" height="24px" />
          </div>
          <Skeleton width="140px" height="15px" className="mt-1" />
          <Skeleton
            width="100%"
            height="280px"
            borderRadius="8px"
            className="mt-3 mb-2"
          />
          </div>
            </Col>
          ))}
        </Row>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {renderChartRows()}
        </DndContext>
      )}
    </div>
  );
};

export default Dashboard;